import { BASE, getCsrfToken } from "./constants";

export interface SocialIntegration {
  id: string;
  value: string | null;
  account_id?: number;
}

export interface ProfileData {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  locale: string;
  company: string;
  industry: string;
  role: string;
  country: string;
  email_confirmed: boolean;
  integrations?: {
    social_list: SocialIntegration[];
    telegram_name?: string;
  };
}

export type UpdateProfilePayload = Partial<Omit<ProfileData, "email">>;

export type UpdateProfileResponse =
  | { ok: true; data: ProfileData }
  | { ok: false; fieldErrors?: Record<string, string[]> };

const getProfile = async (): Promise<ProfileData> => {
  const res = await fetch(`${BASE}/profile/`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const updateProfile = async (
  payload: UpdateProfilePayload,
): Promise<UpdateProfileResponse> => {
  const res = await fetch(`${BASE}/profile/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify(payload),
  });
  if (res.ok) {
    return { ok: true, data: await res.json() };
  }
  if (res.status === 400) {
    const fieldErrors = await res.json().catch(() => undefined);
    return { ok: false, fieldErrors };
  }
  return { ok: false };
};

const deleteProfile = async (): Promise<void> => {
  const res = await fetch(`${BASE}/profile/`, {
    method: "DELETE",
    headers: { "X-CSRFToken": getCsrfToken() },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export interface PublicProfileData {
  email: string;
  username: string;
  date_joined?: string;
  instance_url?: string;
}

const getPublicProfile = async (guid: string): Promise<PublicProfileData> => {
  const res = await fetch(
    `${BASE}/profile/public?guid=${encodeURIComponent(guid)}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export type SendProfileVerificationEmailResponse =
  | { ok: true; token: string }
  | { ok: false };

const sendProfileVerificationEmail =
  async (): Promise<SendProfileVerificationEmailResponse> => {
    const res = await fetch(`${BASE}/auth/profile/email`, {
      method: "PUT",
      headers: { "X-CSRFToken": getCsrfToken() },
    });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, token: data.token };
    }
    return { ok: false };
  };

export {
  deleteProfile,
  getProfile,
  getPublicProfile,
  sendProfileVerificationEmail,
  updateProfile,
};
